import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Headset, Briefcase, DollarSign, Loader2, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (n) => `A$${Math.round(n || 0).toLocaleString()}`;

/**
 * "Pipeline by CS Lead" rollup — count + total value + effort per CS rep.
 * Role-aware: leadership see every rep; a CS rep sees only their own book.
 * Mirrors SalesPipelineInsightsCard; rows deep-link to the Workspaces board
 * pre-filtered by CS Lead.
 */
export const CSPipelineInsightsCard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/api/insights/cs-pipeline`, { withCredentials: true })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const reps = data?.reps || [];
  const maxValue = Math.max(1, ...reps.map((r) => r.total_value || 0));
  const scoped = data?.scoped_to_self;

  return (
    <Card data-testid="cs-pipeline-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 text-[#0F2040]">
          <Headset className="w-4 h-4 text-emerald-600" />
          {scoped ? 'My CS Pipeline' : 'Pipeline by CS Lead'}
        </CardTitle>
        {!loading && data && (
          <p className="text-xs text-slate-400">
            {data.totals?.count || 0} active deal{(data.totals?.count || 0) === 1 ? '' : 's'} ·{' '}
            {fmtMoney(data.totals?.total_value)} total
            {!scoped && data.unassigned_count > 0 && ` · ${data.unassigned_count} unassigned`}
          </p>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-400 py-6 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading pipeline…
          </div>
        ) : reps.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-6 text-center" data-testid="cs-pipeline-empty">
            No active estimates with an assigned CS Lead yet.
          </div>
        ) : (
          <div className="space-y-2.5">
            {reps.map((r) => (
              <button
                key={r.user_id}
                type="button"
                onClick={() => navigate(`/workspaces?cs_lead=${encodeURIComponent(r.user_id)}`)}
                className="w-full flex items-center gap-3 text-left rounded-lg -mx-1.5 px-1.5 py-1 hover:bg-slate-50 transition-colors group focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                data-testid={`cs-pipeline-rep-${r.user_id}`}
                title={`View ${r.name}'s CS deals on the board`}
              >
                <div className="w-7 h-7 rounded-full bg-emerald-50 flex items-center justify-center shrink-0">
                  <Headset className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-sm font-medium text-[#0F2040] truncate flex items-center gap-1">
                      {r.name}
                      <ChevronRight className="w-3 h-3 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </span>
                    <span className="text-sm font-semibold text-[#0F2040] tabular-nums shrink-0">{fmtMoney(r.total_value)}</span>
                  </div>
                  <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-emerald-500"
                      style={{ width: `${Math.max(4, ((r.total_value || 0) / maxValue) * 100)}%` }}
                    />
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-2">
                    <span className="inline-flex items-center gap-0.5"><Briefcase className="w-2.5 h-2.5" />{r.count} deal{r.count === 1 ? '' : 's'}</span>
                    <span className="inline-flex items-center gap-0.5"><DollarSign className="w-2.5 h-2.5" />{Math.round(r.total_effort).toLocaleString()} hrs</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CSPipelineInsightsCard;
